import { useAppSelector } from '@/redux/store';
import { useSuspenseFetch } from '@/api';
import { CPLAT_API_ROUTES } from '@/api';
import { AxiosResponse } from 'axios';
import React, { createContext, useContext } from 'react';

export type RegisteredMarketInfo = {
  MarketAlias: string;
  MarketIconUrl: string;
  MarketId: string;
  MarketName: string;
  MarketNameEnglish: string;
  StoreComment?: string;
};

export type AppNotification = {
  SeqNo: number;
  Title: string;
  Content: string;
  RegisterDate: string;
  IconImageUrl: string;
};

interface ConfigContextType {
  registedMarketList: RegisteredMarketInfo[];
  notificationList: AppNotification[];
}

const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

export const useConfig = () => {
  const context = useContext(ConfigContext);
  if (!context) throw new Error('useConfig must be used within ConfigProvider');
  return context;
};

export const ConfigProvider = ({ children }: { children: React.ReactNode }) => {
  const user = useAppSelector((state) => state.user);

  // 마켓 연결 정보
  const { data: registeredMarketRes } = useSuspenseFetch(CPLAT_API_ROUTES.getRegisteredMarketInfo, {
    method: 'POST',
    params: {
      cplatToken: user.CplatToken,
    },
  });

  // 알림 리스트
  const { data: notificationRes } = useSuspenseFetch(CPLAT_API_ROUTES.getNotificationBox, {
    method: 'POST',
    params: {
      cplatToken: user.CplatToken,
      Skip: 0,
      Take: 50,
      OrderByMethod: 0,
    },
  });

  // 마켓 리스트 파싱
  const registedMarketList =
    (registeredMarketRes as AxiosResponse<{ RegisteredMarketList: RegisteredMarketInfo[] }[]>)
      ?.data?.[0]?.RegisteredMarketList ?? [];

  // 알림 리스트 파싱 (구조 반영)
  const notificationList =
    (notificationRes as AxiosResponse<{ NotificationList: AppNotification[] }[]>)?.data?.[0]
      ?.NotificationList ?? [];

  return (
    <ConfigContext.Provider value={{ registedMarketList, notificationList }}>
      {children}
    </ConfigContext.Provider>
  );
};
